import { t } from '@lingui/macro'

import { isMedicationFileProcessed } from '@/modules/app/utils/medication.utils'
import { MedicationFileProcessed } from './MedicationFileProcessed'
import { MedicationPhotoButton } from './MedicationPhotoButton'
import { MedicationPhotoThumbnail } from './MedicationPhotoThumbnail'

import type { ComponentPropsWithoutRef } from 'react'
import type { MedicationFile } from '@/modules/app/types/HealthParameter.types'

/* ------------------------------------------------------------------------------------------------- *
 * MedicationStoredPhoto
 * ------------------------------------------------------------------------------------------------- */

type MedicationStoredPhotoProps = Omit<ComponentPropsWithoutRef<typeof MedicationPhotoThumbnail>, 'src'> & {
  /** The stored file, or nothing while it is not in yet. */
  file?: MedicationFile
  /** Turns the thumbnail into a button that hands over its url, to open the photo full screen. */
  onOpen?: (url: string) => void
}

/**
 * A stored medication photo: its thumbnail, the stand-in once the care team
 * processed it, or nothing while its url is not in.
 */
const MedicationStoredPhoto = ({ file, onOpen, ...props }: MedicationStoredPhotoProps) => {
  if (isMedicationFileProcessed(file)) {
    return (
      <MedicationFileProcessed>
        <MedicationFileProcessed.Icon icon={['fal', 'image']} />
      </MedicationFileProcessed>
    )
  }

  const url = file?.url
  if (!url) {
    return null
  }

  if (!onOpen) {
    return <MedicationPhotoThumbnail src={url} {...props} />
  }

  return (
    <MedicationPhotoButton
      aria-label={t({
        id: 'medication.photo.open',
        comment: 'Label of a medication photo thumbnail that opens the photo full screen',
        message: 'Bekijk de foto van je medicatie',
      })}
      onClick={() => onOpen(url)}
    >
      <MedicationPhotoThumbnail src={url} {...props} />
    </MedicationPhotoButton>
  )
}

export { MedicationStoredPhoto }
